import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Link,
  redirect,
} from '@tanstack/react-router';
import { Layout } from './components/Layout';
import { AuditPage } from './pages/AuditPage';
import { BuildingsPage } from './pages/BuildingsPage';
import type { CollectionKey, SectionKey } from './lib/contentConfig';
import { CollectionEditorPage, SectionEditorPage } from './pages/ContentEditorPage';
import { DashboardPage } from './pages/DashboardPage';
import { PlayerDetailPage, PlayersPage } from './pages/PlayersPage';
import { PublishPage } from './pages/PublishPage';
import { PurchasesPage } from './pages/PurchasesPage';
import { HealthSourcesPage } from './pages/HealthSourcesPage';
import { PlaytestMetricsPage } from './pages/PlaytestMetricsPage';
import { SavePointsPage } from './pages/SavePointsPage';
import { TriggerCodesPage } from './pages/TriggerCodesPage';
import { TriggerRedemptionsPage } from './pages/TriggerRedemptionsPage';
import { TechTreePage } from './pages/TechTreePage';
import { WhereSpawnPage } from './pages/WhereSpawnPage';
import { VehiclesPage } from './pages/VehiclesPage';
import { OverwatchAttemptsPage } from './pages/OverwatchAttemptsPage';

// Code-based route tree. Switch to file-based routing (@tanstack/router-plugin) if it grows much larger.

const rootRoute = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: Layout,
  notFoundComponent: () => (
    <div>
      <h1 className="text-2xl font-bold">Not found</h1>
      <Link to="/" className="mt-2 inline-block underline">
        Back to the dashboard
      </Link>
    </div>
  ),
});

const page = <TPath extends string>(path: TPath, component: () => React.JSX.Element) =>
  createRoute({ getParentRoute: () => rootRoute, path, component });

const collection = <TPath extends string>(path: TPath, key: CollectionKey) =>
  page(path, () => <CollectionEditorPage key={key} collection={key} />);

const section = <TPath extends string>(path: TPath, key: SectionKey) =>
  page(path, () => <SectionEditorPage key={key} section={key} />);

const playerDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'players/$playerId',
  component: function PlayerRoute() {
    const { playerId } = playerDetailRoute.useParams();
    return <PlayerDetailPage playerId={playerId} />;
  },
});

const routeTree = rootRoute.addChildren([
  page('/', DashboardPage),
  collection('content/items', 'items'),
  collection('content/loot-tables', 'lootTables'),
  collection('content/recipes', 'recipes'),
  collection('content/modules', 'baseModules'),
  collection('content/zombies', 'zombies'),
  collection('content/categories', 'categories'),
  collection('content/rooms', 'roomTemplates'),
  section('content/raid-rules', 'raidRules'),
  section('content/survival', 'survival'),
  section('content/bases', 'bases'),
  section('content/noise', 'noiseRadii'),
  collection('content/traits', 'survivorTraits'),
  section('content/team-rules', 'teamRules'),
  section('content/discovery', 'discovery'),
  section('content/container', 'container'),
  section('content/convoy', 'convoy'),
  section('content/encounters', 'encounters'),
  section('content/world-events', 'worldEvents'),
  section('content/store', 'store'),
  section('content/characters', 'characters'),
  section('content/resurrection', 'resurrection'),
  section('content/squad-rules', 'squadRules'),
  section('content/conditions', 'conditions'),
  collection('content/bosses', 'bosses'),
  collection('content/triggers', 'triggers'),
  collection('content/missions', 'missions'),
  collection('content/cosmetics', 'cosmetics'),
  collection('content/beat-chains', 'beatChains'),
  collection('content/allies', 'allies'),
  section('content/story', 'story'),
  collection('content/scenarios', 'scenarios'),
  collection('content/set-pieces', 'setPieces'),
  collection('content/world-flags', 'worldFlags'),
  collection('content/npcs', 'npcs'),
  collection('content/pronouns', 'pronounSets'),
  section('content/panel-rules', 'panelRules'),
  section('content/chance-events', 'chanceEvents'),
  section('content/occupied', 'occupied'),
  collection('content/crews', 'crews'),
  collection('content/humans', 'humans'),
  collection('content/tells', 'tells'),
  section('content/rescues', 'rescues'),
  collection('content/holdouts', 'holdouts'),
  section('content/holdout-rules', 'holdoutRules'),
  page('content/tech-tree', TechTreePage),
  collection('content/tech-tiers', 'techTiers'),
  section('content/crafting', 'crafting'),
  section('content/field-kit', 'fieldKit'),
  collection('content/site-kinds', 'siteKinds'),
  collection('content/key-parts', 'keyParts'),
  section('content/key-part-rules', 'keyPartRules'),
  page('content/where-would-it-spawn', WhereSpawnPage),
  page('content/vehicles', VehiclesPage),
  collection('content/arena-templates', 'arenaTemplates'),
  section('content/arena-rules', 'arenaRules'),
  section('content/lure-sites', 'lureSites'),
  section('content/overwatch', 'overwatch'),
  collection('content/arcs', 'arcs'),
  section('content/story-issue-rules', 'storyRules'),
  section('content/tutorial-rules', 'tutorialRules'), // ADR 0074: JSON editing (no form yet)
  collection('content/core-stats', 'coreStats'),
  collection('content/skills', 'skills'),
  collection('content/skill-perks', 'perks'),
  section('content/skill-rules', 'skillRules'),
  section('content/names', 'names'),
  collection('content/effects', 'statusEffects'),
  section('content/effect-rules', 'effectRules'),
  section('content/health-rules', 'healthRules'),
  collection('content/zones', 'zones'),
  collection('content/backgrounds', 'backgrounds'),
  section('content/home-rules', 'homeRules'),
  collection('content/storage-groups', 'storageGroups'),
  collection('content/wind-tiers', 'winds'),
  collection('content/build-categories', 'buildCategories'),
  section('content/inventory-rules', 'inventoryRules'),
  section('content/loadout-slots', 'loadoutRules'),
  page('overwatch-attempts', OverwatchAttemptsPage),
  page('publish', PublishPage),
  page('players', PlayersPage),
  playerDetailRoute,
  page('purchases', PurchasesPage),
  page('health/sources', HealthSourcesPage),
  page('playtest', PlaytestMetricsPage),
  page('saves', SavePointsPage),
  page('trigger-codes', TriggerCodesPage),
  page('trigger-redemptions', TriggerRedemptionsPage),
  // The Boss codes page became Trigger codes (ADR 0029 §12); old bookmarks land there.
  createRoute({
    getParentRoute: () => rootRoute,
    path: 'boss-codes',
    beforeLoad: () => {
      throw redirect({ to: '/trigger-codes' });
    },
  }),
  page('buildings', BuildingsPage),
  page('audit', AuditPage),
]);

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
